import { authModule } from '@/store';

/**
 * What a logged-in user still owes before they can bid, and the one-shot flag
 * that stops two components redirecting them at once.
 *
 * The obligations are read straight off the user resource. Both fields are
 * server-computed and read-only:
 *
 * - `isVerified` - they have followed the link in the verification email, or a
 *   staff member vouched for them.
 * - `termsAcceptanceRequired` - keyed off directly and never re-derived by
 *   comparing `acceptedTermsVersion` against the current one. With
 *   FORCE_TERMS_REACCEPTANCE off an older acceptance still counts, so that
 *   comparison would nag people who are perfectly fine.
 *
 * `X-Verification-Reminder` is deliberately *not* consulted here. It says the
 * user is incomplete and still inside the grace window - strictly less than the
 * two fields above already say, apart from the window itself, and it is absent
 * for staff. Driving the banner off it would hide the notice from exactly the
 * users the window has closed on. See `api/verificationReminder.ts`.
 */
export interface Obligations {
  /** They have not confirmed their email address. */
  verification: boolean;
  /** They are being asked to accept the current terms. */
  terms: boolean;
  /** Either of the above. */
  any: boolean;
}

export function outstandingObligations(): Obligations {
  const user = authModule.user;
  if (!authModule.isLoggedIn || !user) {
    return { verification: false, terms: false, any: false };
  }
  const verification = !user.isVerified;
  const terms = !!user.termsAcceptanceRequired;
  return { verification, terms, any: verification || terms };
}

/**
 * How many days out the copy starts naming the deadline.
 *
 * An SPA constant on purpose: the server sends the date, not the urgency. One
 * place decides when "keeps your account ready for bidding" becomes "you have
 * three days", and it is a wording decision, not a deployment one.
 */
export const DEADLINE_NOTICE_DAYS = 7;

/**
 * How many days out the notices turn red.
 *
 * A wording/severity decision like `DEADLINE_NOTICE_DAYS`, and for the same
 * reason it lives here rather than on the server: the server sends a date, the
 * SPA decides how loudly to say it.
 */
export const DEADLINE_URGENT_DAYS = 2;

/** The grace window, as the copy needs to think about it. */
export type GracePeriod =
  /** No deadline concerns this reader. Say nothing about timing. */
  | { kind: 'none' }
  /**
   * The window is open; `lastDay` is its final day, inclusive.
   *
   * `soon` is "close enough to name a date at all", `urgent` is "close enough to
   * shout about". Both are derived here so the two thresholds sit side by side;
   * a reader of one should see the other.
   */
  | {
      kind: 'open';
      lastDay: string;
      daysLeft: number;
      soon: boolean;
      urgent: boolean;
    }
  /** The window has passed and bidding is gated. */
  | { kind: 'closed' };

/**
 * Read the user's `gracePeriod` attribute into something with a shape.
 *
 * Everything else reads this, so the `'closed'` magic string is understood in
 * exactly one place.
 *
 * **The server decides open vs closed, never us.** That is what `'closed'` is
 * for. We compute only the day count, for display. Comparing the date against
 * the browser's clock would flip a bidder in another timezone a day early or
 * late at exactly the boundary that matters.
 *
 * `daysLeft` is 0 on the last day itself, because the date is inclusive: 0
 * means "today is the last day", never "expired". Nothing here can return
 * `open` with a negative count - a passed date arrives as `'closed'`.
 *
 * Staleness is accepted, not polled for. The user resource loads on login and
 * on reload, so a tab left open across the boundary keeps saying "1 day left"
 * after the window has shut. It degrades safely: the bid is still refused
 * server-side and `VerificationGateDialog` opens as it does today.
 */
export function gracePeriod(): GracePeriod {
  const user = authModule.user;
  if (!authModule.isLoggedIn || !user || !user.gracePeriod) {
    return { kind: 'none' };
  }
  if (user.gracePeriod === 'closed') {
    return { kind: 'closed' };
  }
  const lastDay = parseIsoDate(user.gracePeriod);
  if (!lastDay) {
    // An unparseable value is treated as no deadline rather than as a crash or
    // a bogus countdown. The server-side gate is unaffected either way.
    return { kind: 'none' };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = Math.max(
    0,
    Math.round((lastDay.getTime() - today.getTime()) / 86400000),
  );
  return {
    kind: 'open',
    lastDay: user.gracePeriod,
    daysLeft,
    soon: daysLeft <= DEADLINE_NOTICE_DAYS,
    urgent: daysLeft <= DEADLINE_URGENT_DAYS,
  };
}

/**
 * `"2026-09-30"` as local midnight, or null.
 *
 * Built from the parts rather than handed to `new Date(string)`, which parses a
 * bare date as UTC midnight - the previous day in Australian time, so every
 * countdown would be off by one.
 */
function parseIsoDate(value: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) {
    return null;
  }
  const [, year, month, day] = parts;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * The deadline as a whole sentence, or '' when there is nothing to say.
 *
 * Shared by the banner and the notices page so the two cannot drift, and the
 * edges are the whole reason it exists: the last day is inclusive, so it reads
 * "ends today" rather than "0 days left" or "expired".
 *
 * It returns a full sentence rather than a fragment on purpose. It used to hand
 * back "today is the last day" for the call sites to frame as "Our grace period
 * ends soon, {phrase}." - which read as a comma splice and, on the last day,
 * contradicted itself: "soon" and "today" are different claims. Framing that has
 * to change with the number is the phrase's own job, not the template's.
 */
export function deadlinePhrase(): string {
  const grace = gracePeriod();
  if (grace.kind !== 'open' || !grace.soon) {
    return '';
  }
  if (grace.daysLeft === 0) {
    return 'Our grace period ends today.';
  }
  if (grace.daysLeft === 1) {
    return 'Our grace period ends tomorrow.';
  }
  return `Our grace period ends in ${grace.daysLeft} days.`;
}

/**
 * How loudly to say it: a Vuetify colour both surfaces share.
 *
 * One escalation for the banner and the notices page, so a user cannot see a red
 * page under a blue banner. Driven by the deadline alone, never by *which*
 * obligation is outstanding - owing both is not more urgent than owing one, it
 * is the same wall on the same date.
 *
 * - `info` - no deadline concerns this reader, or it is further out than
 *   `DEADLINE_NOTICE_DAYS`. The routine-housekeeping tone.
 * - `warning` - inside `DEADLINE_NOTICE_DAYS`, which is also when the copy
 *   starts naming the date at all.
 * - `error` - inside `DEADLINE_URGENT_DAYS`, or the window has already closed.
 *
 * "error" is a poor name for "this is urgent" - nothing has gone wrong with the
 * account - but it is what Vuetify calls its red, and inventing a synonym here
 * would just mean translating at every call site.
 *
 * This escalation replaces P4-09's original "mild but undismissable" pairing,
 * where the banner deliberately kept one tone throughout and relied on
 * persistence alone. Changed on review: a colour costs a passing reader nothing
 * and is the cheapest way to distinguish three months out from tomorrow. The
 * undismissable-once-closed behaviour stays.
 */
export type NoticeSeverity = 'info' | 'warning' | 'error';

export function noticeSeverity(): NoticeSeverity {
  const grace = gracePeriod();
  if (grace.kind === 'closed') {
    return 'error';
  }
  if (grace.kind === 'none') {
    return 'info';
  }
  if (grace.urgent) {
    return 'error';
  }
  return grace.soon ? 'warning' : 'info';
}

/**
 * Set for the instant between the login page deciding to send someone to their
 * notices and the router getting them there.
 *
 * `App.vue` watches `isLoggedIn` so it can honour a `?next=` deep link after a
 * login, and that watcher fires for this login too. Without this flag the two
 * would race - Vue flushes watchers on nextTick and a router navigation settles
 * in its own microtask, so which one won would be luck.
 *
 * `Login.vue` sets it synchronously, inside the same tick as the login itself,
 * so the watcher is guaranteed to see it when it eventually runs. The deep link
 * is not lost: it is carried to the notices page as `?continue=`, which the page
 * offers as a button.
 */
let redirectingToNotices = false;

export function claimLoginRedirect() {
  redirectingToNotices = true;
}

/** True once, for the watcher that has to stand down. */
export function consumeLoginRedirect(): boolean {
  const claimed = redirectingToNotices;
  redirectingToNotices = false;
  return claimed;
}
